import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate, copyToClipboard } from '../../utils/helpers';
import { Plus, Copy } from 'lucide-react';

export const AdminVouchersPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ currency: '', value: '', expiry_days: 365, type: 'platform' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        adminAPI.getVouchers(),
        publicAPI.getCurrencies(true)
      ]);
      setVouchers(vRes.data);
      setCurrencies(cRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await adminAPI.createVoucher({
        currency: form.currency,
        value: parseFloat(form.value),
        type: form.type,
        expiry_days: parseInt(form.expiry_days)
      });
      toast.success(`Voucher created: ${data.code}`);
      setDialogOpen(false);
      setForm({ currency: '', value: '', expiry_days: 365, type: 'platform' });
      load();
    } catch (error) {
      toast.error('Failed to create');
    }
  };

  const copy = async (code) => {
    await copyToClipboard(code);
    toast.success('Code copied!');
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-vouchers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Vouchers</h1>
            <p className="mt-2 text-muted-foreground">Create and manage platform vouchers</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-voucher-button">
            <Plus className="h-4 w-4 mr-1" /> Create Voucher
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>All Vouchers ({vouchers.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vouchers.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-mono text-sm">
                        <button onClick={() => copy(v.code)} className="hover:underline flex items-center gap-1">
                          {v.code} <Copy className="h-3 w-3" />
                        </button>
                      </TableCell>
                      <TableCell>{v.value}</TableCell>
                      <TableCell className="font-semibold">{v.currency}</TableCell>
                      <TableCell className="capitalize">{v.type}</TableCell>
                      <TableCell>
                        <Badge className={v.status === 'active' ? 'bg-green-600' : v.status === 'redeemed' ? 'bg-blue-600' : 'bg-gray-500'}>
                          {v.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{formatDate(v.created_at)}</TableCell>
                      <TableCell className="text-xs">{formatDate(v.expires_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Voucher</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                  <SelectTrigger data-testid="voucher-currency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value</Label>
                <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} required data-testid="voucher-value-input" />
              </div>
              <div className="space-y-2">
                <Label>Expiry (days)</Label>
                <Input type="number" value={form.expiry_days} onChange={(e) => setForm({...form, expiry_days: e.target.value})} required />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-voucher-button">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
